/**
 * reportCsv.ts
 *
 * Builds the CSV download of a Title Analysis report. Pure (no React).
 *
 * The file holds the whole report as titled sections, each a header row
 * followed by its data rows, with a blank row between sections:
 * Report, Exceptions, Vesting chain, Parties, Encumbrances, Assignment chains.
 * Lists are joined with "; ". Party IDs are shown with their names. Amounts and
 * dates are written as stored, so a spreadsheet can sort and sum them.
 */

import { USE_CASE_LABELS } from './inputs';
import type { TitleAnalysisReport } from './types';

type Cell = string | number | boolean | null | undefined;

const SEVERITY_ORDER: readonly string[] = ['material', 'curable', 'informational'];

/**
 * One CSV cell. Values are quoted when they hold a comma, quote or line break.
 * Text that starts with `=`, `+`, `-`, `@`, tab or carriage return gets a
 * leading apostrophe so a spreadsheet does not run it as a formula (the values
 * come from extracted documents). Numbers are written as they are.
 */
function csvCell(value: Cell): string {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const capitalise = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const words = (value: string): string => capitalise(value.replace(/_/g, ' '));

const yesNo = (value: boolean | null | undefined): string =>
  value == null ? '' : value ? 'Yes' : 'No';

const join = (items: readonly string[] | null | undefined): string => (items ?? []).join('; ');

/** The report as CSV text. Starts with a byte order mark so Excel reads it as UTF-8. */
export function buildReportCsv(
  report: TitleAnalysisReport,
  loanNumber: string | null | undefined
): string {
  const names = new Map(report.entity_resolution.map((e) => [e.canonical_party_id, e.canonical_name]));
  const partyNames = (ids: readonly string[]): string => ids.map((id) => names.get(id) ?? id).join('; ');

  const lines: Cell[][] = [];
  const section = (title: string, header: Cell[], rows: Cell[][]) => {
    if (lines.length > 0) lines.push([]);
    lines.push([title], header, ...rows);
  };

  const { property, current_vested_owner: owner, subject_lien_position: lien } = report;
  const { search_coverage: coverage, commitment_reconciliation: commitment } = report;

  section('Report', ['Field', 'Value'], [
    ['Loan number', loanNumber ?? ''],
    ['Loan ID', report.loan_id],
    ['Analysis date', report.analysis_date],
    ['Use case', USE_CASE_LABELS[report.use_case] ?? report.use_case],
    ['Title grade', report.title_grade],
    ['Grade rationale', report.grade_rationale],
    ['Summary', report.summary],
    ['Property APN', property.apn],
    ['Property address', property.address],
    ['Legal description consistent', yesNo(property.legal_description_consistent)],
    ['Property notes', property.notes],
    ['Current vested owner party IDs', join(owner.party_ids)],
    ['Current vested owner', partyNames(owner.party_ids)],
    ['Matches subject mortgagor', yesNo(owner.matches_subject_mortgagor)],
    ['Vested owner notes', owner.notes],
    ['Lien position expected', lien.expected],
    ['Lien position derived', lien.derived],
    ['Lien position status', capitalise(lien.status)],
    ['Senior open encumbrance IDs', join(lien.senior_open_encumbrance_ids)],
    ['Lien position explanation', lien.explanation],
    ['Search start date', coverage.search_start_date],
    ['Search effective date', coverage.search_effective_date],
    ['Meets minimum search period', yesNo(coverage.meets_minimum_period)],
    ['Bring-down gap (days)', coverage.bring_down_gap_days],
    ['Search coverage gaps', join(coverage.gaps)],
    ['Commitment provided', yesNo(commitment.provided)],
    ['Commitment vesting matches', yesNo(commitment.vesting_matches)],
    ['Commitment insured matches subject loan', yesNo(commitment.insured_matches_subject_loan)],
    ['Exceptions missing from Schedule B', join(commitment.exceptions_missing_from_schedule_b)],
    ['Schedule B items not found in search', join(commitment.schedule_b_items_not_found_in_search)],
    ['Commitment notes', commitment.notes],
  ]);

  const rank = (severity: string) => {
    const i = SEVERITY_ORDER.indexOf(severity);
    return i === -1 ? SEVERITY_ORDER.length : i;
  };
  const exceptions = [...report.exceptions].sort((a, b) => rank(a.severity) - rank(b.severity));
  section(
    'Exceptions',
    [
      'Exception ID',
      'Rule code',
      'Category',
      'Severity',
      'Title',
      'Description',
      'Evidence',
      'Confidence',
      'Curative action',
      'Requires human review',
      'Related exception IDs',
      'Assumptions',
    ],
    exceptions.map((e) => [
      e.exception_id,
      e.rule_code,
      capitalise(e.category),
      capitalise(e.severity),
      e.title,
      e.description,
      e.evidence
        .map((ev) => {
          const where = [ev.instrument_id, ev.page != null ? `p.${ev.page}` : null]
            .filter(Boolean)
            .join(' ');
          return where ? `${where} - ${ev.fact}` : ev.fact;
        })
        .join('\n'),
      capitalise(e.confidence),
      e.curative_action,
      yesNo(e.requires_human_review),
      join(e.related_exception_ids),
      join(e.assumptions),
    ])
  );

  section(
    'Vesting chain',
    [
      'Sequence',
      'Instrument ID',
      'Conveyance type',
      'Grantor party IDs',
      'Grantors',
      'Grantee party IDs',
      'Grantees',
      'Executed date',
      'Recorded date',
      'Consideration',
      'Link to prior',
    ],
    [...report.vesting_chain]
      .sort((a, b) => a.sequence - b.sequence)
      .map((l) => [
        l.sequence,
        l.instrument_id,
        l.conveyance_type,
        join(l.grantor_party_ids),
        partyNames(l.grantor_party_ids),
        join(l.grantee_party_ids),
        partyNames(l.grantee_party_ids),
        l.executed_date,
        l.recorded_date,
        l.consideration,
        words(l.link_to_prior),
      ])
  );

  section(
    'Parties',
    ['Party ID', 'Name', 'Name variants', 'Confidence', 'Basis'],
    report.entity_resolution.map((p) => [
      p.canonical_party_id,
      p.canonical_name,
      join(p.variants),
      capitalise(p.confidence),
      p.basis,
    ])
  );

  section(
    'Encumbrances',
    [
      'Encumbrance ID',
      'Type',
      'Instrument ID',
      'Original holder',
      'Current holder of record',
      'Debtor party IDs',
      'Debtors',
      'Amount',
      'Recorded date',
      'Status',
      'Status basis',
      'Priority rank',
      'Subject mortgage',
    ],
    report.encumbrances.map((enc) => [
      enc.encumbrance_id,
      enc.type,
      enc.instrument_id,
      enc.original_holder,
      enc.current_holder_of_record,
      join(enc.debtor_party_ids),
      partyNames(enc.debtor_party_ids),
      enc.amount,
      enc.recorded_date,
      words(enc.status),
      enc.status_basis,
      enc.derived_priority_rank,
      yesNo(enc.is_subject_mortgage),
    ])
  );

  section(
    'Assignment chains',
    ['Encumbrance ID', 'Step', 'Instrument ID', 'Assignor', 'Assignee', 'Recorded date', 'Link valid'],
    report.encumbrances.flatMap((enc) =>
      enc.assignment_chain.map((a, i) => [
        enc.encumbrance_id,
        i + 1,
        a.instrument_id,
        a.assignor,
        a.assignee,
        a.recorded_date,
        yesNo(a.link_valid),
      ])
    )
  );

  const text = lines.map((row) => row.map(csvCell).join(',')).join('\r\n');
  return `\uFEFF${text}\r\n`;
}
